import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useOfferUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { EToasterStatus, showToast } from 'utils/showToast';
import { useOfferDesignColumns } from 'views/OfferDesign/hooks/useOfferDesignColumns';
import { useOfferDesignFilters } from 'views/OfferDesign/hooks/useOfferDesignFilters';
import { BUTTON_TEXT } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignRow } from 'views/OfferDesign/OfferDesign.types';

import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';
import PortalHeader from 'components/PortalHeader/PortalHeader';

import { Filters } from './components/Filters';
import { TableContent } from './components/TableContent';

const OfferDesignTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { getOffersUI, deleteOfferUI } = useOfferUI(publisherId);
  const { canUserEdit } = usePermissions();
  const { isActionEnabled } = useUserPermissions();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOfferUI, setSelectedOfferUI] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };
  }, [isDeleteModalOpen]);

  const canManage = isActionEnabled(
    null,
    EPermissionGroup.OFFER_DESIGN,
    EPermissionAction.MANAGE,
    publisherId
  );

  const offers: OfferDesignRow[] = useMemo(() => {
    if (getOffersUI.isLoading || !getOffersUI.data) return [];
    return getOffersUI.data.result;
  }, [getOffersUI.data, getOffersUI.isLoading]);

  const {
    externalIdFilter,
    nameFilter,
    typeFilter,
    filteredOffers,
    handleExternalIdChange,
    handleNameChange,
    handleTypeChange,
    handleClearFilters
  } = useOfferDesignFilters(offers);

  const actions = useMemo(
    () => ({
      onEdit: (id: string, offerUiType: string) => navigate(`./form/${id}?type=${offerUiType}`),
      onDuplicate: (id: string, offerUiType: string) => navigate(`./dup/${id}?type=${offerUiType}`),
      onDelete: (id: string) => {
        setSelectedOfferUI(id);
        setIsDeleteModalOpen(true);
      }
    }),
    [navigate]
  );

  const permissions = useMemo(
    () => ({ canUserEdit, canManage }),
    [canUserEdit, canManage]
  );

  const { columns } = useOfferDesignColumns({
    isLoading: getOffersUI.isLoading,
    actions,
    permissions
  });

  const handleCreate = () => navigate('./select-type');

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDeleteOfferUI = useCallback(async () => {
    if (!selectedOfferUI) return;
    await deleteOfferUI.mutate(selectedOfferUI, {
      onSuccess: () => {
        showToast({
          message: 'Offer Design deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffersUI.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete Offer Design',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setIsDeleteModalOpen(false);
        setSelectedOfferUI(null);
      }
    });
  }, [selectedOfferUI, deleteOfferUI, getOffersUI]);

  return (
    <div className="overflow-y-scroll opacity-100 pointer-events-auto">
      <PortalHeader
        title="Offer Design"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={handleCreate}
        primaryButtonState={{
          disabled: !canUserEdit,
          hidden: !canManage
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <Filters
          externalIdFilter={externalIdFilter}
          nameFilter={nameFilter}
          typeFilter={typeFilter}
          onExternalIdChange={handleExternalIdChange}
          onNameChange={handleNameChange}
          onTypeChange={handleTypeChange}
          onClearFilters={handleClearFilters}
        />
      )}
      <TableContent
        isLoading={getOffersUI.isLoading || getOffersUI.isFetching}
        offers={offers}
        filteredOffers={filteredOffers}
        columns={columns}
        canManage={canManage}
        onCreate={handleCreate}
      />
      {isDeleteModalOpen && (
        <DialogModal
          isOpen={isDeleteModalOpen}
          onOpenChange={handleCloseDeleteModal}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_UI_POPUP].title}
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_UI_POPUP].description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOfferUI },
            onClose: {
              label: 'Cancel',
              action: handleCloseDeleteModal,
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default OfferDesignTable;
